import React from 'react'
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer'
import { RBC_BRANDING } from '../lib/proposalDocumentTemplates'

export interface InvoicePdfLineItem {
  description: string
  quantity: number
  unit_price: number
  total: number
}

export interface InvoiceDocumentProps {
  invoiceNumber: string
  invoiceDate?: string | null
  dueDate?: string | null
  status: string
  customerName: string
  lineItems: InvoicePdfLineItem[]
  subtotal: number
  amountPaid?: number
  balanceDue?: number
  notes?: string | null
}

const BRAND_BLUE = '#168EB8'
const INK = '#1a1a1a'
const MUTED = '#555555'
const RULE = '#d6dde5'
const LOGO_URL = '/rbc-logo.png'

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingHorizontal: 46,
    paddingBottom: 60,
    fontFamily: 'Times-Roman',
    fontSize: 10,
    color: INK,
    lineHeight: 1.4,
  },
  letterhead: {
    borderBottomWidth: 2,
    borderBottomColor: BRAND_BLUE,
    paddingBottom: 10,
    marginBottom: 18,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  logo: { width: 150, height: 40 },
  letterheadAddr: { fontSize: 8.5, color: MUTED, textAlign: 'right' },
  title: { fontSize: 20, fontFamily: 'Times-Bold', color: BRAND_BLUE },
  invoiceNumber: { fontSize: 11, color: MUTED, marginTop: 2 },
  metaRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 18 },
  metaLabel: { fontSize: 8.5, color: MUTED, marginBottom: 2 },
  metaValue: { fontSize: 10.5, fontFamily: 'Times-Bold' },
  statusBadge: { fontSize: 9, fontFamily: 'Times-Bold', color: BRAND_BLUE, textTransform: 'uppercase' },
  table: { borderWidth: 1, borderColor: RULE, marginTop: 6 },
  tr: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: RULE },
  trLast: { flexDirection: 'row' },
  th: { backgroundColor: '#f1f5f9', fontFamily: 'Times-Bold', fontSize: 9.5, padding: 5 },
  td: { fontSize: 9.5, padding: 5 },
  cellRight: { textAlign: 'right' },
  cellBorderLeft: { borderLeftWidth: 1, borderLeftColor: RULE },
  totalsBlock: { marginTop: 12, alignItems: 'flex-end' },
  totalsRow: { flexDirection: 'row', width: 220, justifyContent: 'space-between', marginBottom: 3 },
  totalsLabel: { fontSize: 10, color: MUTED },
  totalsValue: { fontSize: 10, fontFamily: 'Times-Bold' },
  grandTotalRow: {
    flexDirection: 'row',
    width: 220,
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: INK,
    paddingTop: 4,
    marginTop: 4,
  },
  notes: { marginTop: 20, fontSize: 9, color: MUTED },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 46,
    right: 46,
    textAlign: 'center',
    fontSize: 8,
    color: '#999999',
  },
})

function money(n: number): string {
  return `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function formatDate(date?: string | null): string {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export const InvoiceDocumentPdf: React.FC<InvoiceDocumentProps> = ({
  invoiceNumber,
  invoiceDate,
  dueDate,
  status,
  customerName,
  lineItems,
  subtotal,
  amountPaid = 0,
  balanceDue,
  notes,
}) => {
  const resolvedBalance = balanceDue ?? Math.max(0, subtotal - amountPaid)

  return (
    <Document title={`Invoice ${invoiceNumber}`} author={RBC_BRANDING.companyName}>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.letterhead}>
          {/* eslint-disable-next-line jsx-a11y/alt-text */}
          <Image style={styles.logo} src={LOGO_URL} />
          <Text style={styles.letterheadAddr}>
            {RBC_BRANDING.companyAddressLines.join('\n')}
            {'\n'}
            {RBC_BRANDING.repPhone}
          </Text>
        </View>

        <View style={styles.metaRow}>
          <View>
            <Text style={styles.title}>INVOICE</Text>
            <Text style={styles.invoiceNumber}>{invoiceNumber}</Text>
            <Text style={[styles.statusBadge, { marginTop: 6 }]}>{status}</Text>
          </View>
          <View>
            <Text style={styles.metaLabel}>Bill To</Text>
            <Text style={styles.metaValue}>{customerName}</Text>
            <View style={{ height: 8 }} />
            <Text style={styles.metaLabel}>Issue Date</Text>
            <Text style={styles.metaValue}>{formatDate(invoiceDate)}</Text>
            <View style={{ height: 8 }} />
            <Text style={styles.metaLabel}>Due Date</Text>
            <Text style={styles.metaValue}>{formatDate(dueDate)}</Text>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.tr}>
            <Text style={[styles.th, { flex: 3 }]}>Description</Text>
            <Text style={[styles.th, styles.cellRight, styles.cellBorderLeft, { flex: 1 }]}>Qty</Text>
            <Text style={[styles.th, styles.cellRight, styles.cellBorderLeft, { flex: 1 }]}>Unit Price</Text>
            <Text style={[styles.th, styles.cellRight, styles.cellBorderLeft, { flex: 1 }]}>Total</Text>
          </View>
          {lineItems.length === 0 ? (
            <View style={styles.trLast}>
              <Text style={[styles.td, { flex: 6 }]}>No line items.</Text>
            </View>
          ) : (
            lineItems.map((li, i) => {
              const last = i === lineItems.length - 1
              return (
                <View key={i} style={last ? styles.trLast : styles.tr}>
                  <Text style={[styles.td, { flex: 3 }]}>{li.description}</Text>
                  <Text style={[styles.td, styles.cellRight, styles.cellBorderLeft, { flex: 1 }]}>
                    {li.quantity}
                  </Text>
                  <Text style={[styles.td, styles.cellRight, styles.cellBorderLeft, { flex: 1 }]}>
                    {money(li.unit_price)}
                  </Text>
                  <Text style={[styles.td, styles.cellRight, styles.cellBorderLeft, { flex: 1 }]}>
                    {money(li.total)}
                  </Text>
                </View>
              )
            })
          )}
        </View>

        <View style={styles.totalsBlock}>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Subtotal</Text>
            <Text style={styles.totalsValue}>{money(subtotal)}</Text>
          </View>
          {amountPaid > 0 && (
            <View style={styles.totalsRow}>
              <Text style={styles.totalsLabel}>Paid</Text>
              <Text style={styles.totalsValue}>{money(amountPaid)}</Text>
            </View>
          )}
          <View style={styles.grandTotalRow}>
            <Text style={[styles.totalsLabel, { fontFamily: 'Times-Bold' }]}>Balance Due</Text>
            <Text style={[styles.totalsValue, { fontSize: 12 }]}>{money(resolvedBalance)}</Text>
          </View>
        </View>

        {notes ? (
          <View style={styles.notes}>
            <Text>{notes}</Text>
          </View>
        ) : null}

        <Text
          style={styles.footer}
          render={() => `${RBC_BRANDING.companyName} · ${RBC_BRANDING.companyAddress} · ${RBC_BRANDING.repPhone}`}
        />
      </Page>
    </Document>
  )
}

export default InvoiceDocumentPdf
